/**
 * Bloomreach subscriber for the generic tracking emitter.
 *
 * Maps vendor-agnostic business events to Bloomreach SDK calls.
 * Add new event mappings here as tracking requirements are defined.
 *
 * IMPORTANT: This module subscribes eagerly at import time (bottom of file).
 * This is intentional. SSR-hydrated components (lister, PDP) emit events
 * before the Bootstrap component mounts and calls initBloomreach().
 * Without eager subscription, those events would be lost on the generic emitter.
 * Events emitted before init are queued in bloomreachClient and flushed once
 * the SDK is initialized.
 */
import { emitter, EmitterEvent } from '../emitter';
import { anonymizeBloomreach, trackBloomreach } from './bloomreachClient';
import { BloomreachEventNames } from './constants';
// Partial, not a full Record — Bloomreach only needs to handle a subset of the
// vendor-agnostic events; other vendors (e.g. Fredhopper) may define events it
// doesn't care about.
const EVENT_MAP = {
    [EmitterEvent.VIEW_ITEM]: data => {
        // Whitelisted, not a passthrough of `data`. Some fields were added to this shared payload
        // for Fredhopper only; picking explicit keys keeps them out of Bloomreach's event
        // regardless of what other vendors add to the payload later.
        const { product_id, sku_id, default_sku, buying_group, product_category_level_1, product_category_level_2, product_category_level_3, product_category_path, price, brand, title, colour, colour_code, size, gender, stock, } = data;
        trackBloomreach(BloomreachEventNames.VIEW_ITEM, {
            product_id,
            sku_id,
            default_sku,
            buying_group,
            product_category_level_1,
            product_category_level_2,
            product_category_level_3,
            product_category_path,
            price,
            brand,
            title,
            colour,
            colour_code,
            size,
            gender,
            stock,
        });
    },
    [EmitterEvent.CATEGORY_VIEW]: data => {
        // Whitelisted for the same reason as above
        const { category_1, category_2, category_3, category_name, domain } = data;
        trackBloomreach(BloomreachEventNames.CATEGORY_VIEW, {
            category_1,
            category_2,
            category_3,
            category_name,
            domain,
        });
    },
    [EmitterEvent.CART_UPDATE]: data => {
        // Whitelisted for the same reason as above
        const { action, product_id, sku_id, product_list, product_ids, buying_group, price, total_price, total_quantity, title, brand, colour, colour_code, gender, size, basket_token, } = data;
        trackBloomreach(BloomreachEventNames.CART_UPDATE, {
            action,
            product_id,
            sku_id,
            product_list,
            product_ids,
            buying_group,
            price,
            total_price,
            total_quantity,
            title,
            brand,
            colour,
            colour_code,
            gender,
            size,
            basket_token,
        });
    },
    [EmitterEvent.LOGOUT]: () => {
        anonymizeBloomreach();
    },
};
let subscribed = false;
/** Subscribes to the generic tracking emitter. */
export const subscribeBloomreachToTracking = () => {
    if (subscribed) {
        return;
    }
    subscribed = true;
    Object.entries(EVENT_MAP).forEach(([event, handler]) => {
        emitter.on(event, handler);
    });
};
// Subscribe eagerly so SSR-hydrated components (lister, PDP) can emit events
// before Bootstrap mounts. Events are queued in bloomreachClient until init.
subscribeBloomreachToTracking();
/** Unsubscribes from the generic tracking emitter. */
export const unsubscribeBloomreachFromTracking = () => {
    Object.entries(EVENT_MAP).forEach(([event, handler]) => {
        emitter.off(event, handler);
    });
    subscribed = false;
};
